// /know-how/ page controller. Four responsibilities, all scoped to the
// page root so the module is a no-op on every other route:
//
//  1. Heading letter-by-letter reveal on first paint (Casablanca-style).
//  2. Scroll-triggered reveal of [data-reveal] sections + tiles + steps.
//     One IntersectionObserver, unobserves after firing — no lingering work.
//  3. Image grid lightbox: click tile → fullscreen overlay with prev/next,
//     keyboard nav, Esc to close, backdrop click to dismiss. Single reusable
//     overlay element appended to <body> on first open.
//  4. Manufacturing video play/pause toggle — same controller as the home
//     hero: a manual pause sticks (userPaused flag), offscreen-pause via
//     IntersectionObserver.

export function initKnowHowPage() {
	const root = document.querySelector('.sentiments-know-how-page');
	if (!root) return;

	initHeadingReveal(root);
	initScrollReveal(root);
	initLightbox(root);
	initVideoToggle(root);
}

// ─── Heading letter-by-letter reveal ───────────────────────────────────────
//
// Split the masthead heading into per-character <span>s so each can carry
// its own `--char-delay`. CSS owns the animation; this just builds the
// structure and triggers the .is-revealed class on the next frame.

function initHeadingReveal(root) {
	const heading = root.querySelector('.sentiments-know-how-page__heading');
	if (!heading) return;

	const text = heading.textContent.trim();
	if (!text) return;

	// Rebuild the heading's contents with per-character spans. The original
	// text was already an aria-label on the heading, so screen readers
	// continue to read "Know How" as a single phrase.
	heading.textContent = '';
	const chars = Array.from(text);
	chars.forEach((ch, i) => {
		const span = document.createElement('span');
		span.className = 'sentiments-know-how-page__heading-char';
		span.setAttribute('aria-hidden', 'true');
		span.style.setProperty('--char-delay', `${i * 55}ms`);
		// Render real space; non-breaking only when needed to preserve width
		// in inline-block context.
		span.textContent = ch === ' ' ? ' ' : ch;
		heading.appendChild(span);
	});

	// Next-frame trigger so the initial state paints before transitions.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => heading.classList.add('is-revealed'));
	});
}

// ─── Scroll reveal ─────────────────────────────────────────────────────────

function initScrollReveal(root) {
	const targets = root.querySelectorAll('[data-reveal]');
	if (!targets.length) return;

	if (typeof IntersectionObserver !== 'function') {
		// Graceful degradation: just show everything.
		targets.forEach((el) => el.classList.add('is-revealed'));
		return;
	}

	const io = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-revealed');
					io.unobserve(entry.target);
				}
			}
		},
		{ threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
	);

	targets.forEach((el) => io.observe(el));
}

// ─── Lightbox ──────────────────────────────────────────────────────────────

function initLightbox(root) {
	const tiles = Array.from(root.querySelectorAll('.sentiments-know-how-page__tile'));
	if (!tiles.length) return;

	const lightbox = buildLightbox();
	document.body.appendChild(lightbox);

	const avifSrc  = lightbox.querySelector('.sentiments-knowhow-lightbox__avif');
	const webpSrc  = lightbox.querySelector('.sentiments-knowhow-lightbox__webp');
	const img      = lightbox.querySelector('.sentiments-knowhow-lightbox__img');
	const counter  = lightbox.querySelector('.sentiments-knowhow-lightbox__counter');
	const closeBtn = lightbox.querySelector('.sentiments-knowhow-lightbox__close');
	const prevBtn  = lightbox.querySelector('.sentiments-knowhow-lightbox__prev');
	const nextBtn  = lightbox.querySelector('.sentiments-knowhow-lightbox__next');

	let currentIndex = 0;
	let lastFocus    = null;

	const render = () => {
		const tile = tiles[currentIndex];
		const fullAvif = tile.dataset.fullAvif;
		const fullWebp = tile.dataset.fullWebp;
		const alt      = tile.dataset.alt || '';
		avifSrc.setAttribute('srcset', fullAvif);
		webpSrc.setAttribute('srcset', fullWebp);
		img.setAttribute('src', fullWebp);
		img.setAttribute('alt', alt);
		counter.textContent = `${String(currentIndex + 1).padStart(2, '0')} / ${String(tiles.length).padStart(2, '0')}`;
	};

	const open = (index) => {
		currentIndex = index;
		lastFocus = document.activeElement;
		render();
		lightbox.hidden = false;
		document.documentElement.style.overflow = 'hidden';
		requestAnimationFrame(() => {
			lightbox.classList.add('is-open');
			closeBtn.focus();
		});
	};

	const close = () => {
		lightbox.classList.remove('is-open');
		setTimeout(() => {
			lightbox.hidden = true;
			document.documentElement.style.overflow = '';
			if (lastFocus && typeof lastFocus.focus === 'function') {
				lastFocus.focus();
			}
		}, 320);
	};

	// Quick crossfade on navigation — image fades to 0 → src swaps → fades in.
	const swap = (nextIndex) => {
		currentIndex = nextIndex;
		// Step the image back to its entrance state, then re-render + animate.
		img.style.opacity = '0';
		img.style.transform = 'scale(0.98)';
		setTimeout(() => {
			render();
			requestAnimationFrame(() => {
				img.style.opacity = '';
				img.style.transform = '';
			});
		}, 160);
	};

	const prev = () => swap((currentIndex - 1 + tiles.length) % tiles.length);
	const next = () => swap((currentIndex + 1) % tiles.length);

	tiles.forEach((tile, i) => {
		tile.addEventListener('click', (e) => {
			e.preventDefault();
			open(i);
		});
	});

	closeBtn.addEventListener('click', close);
	prevBtn.addEventListener('click', (e) => { e.stopPropagation(); prev(); });
	nextBtn.addEventListener('click', (e) => { e.stopPropagation(); next(); });

	lightbox.addEventListener('click', (e) => {
		if (e.target === lightbox) close();
	});

	document.addEventListener('keydown', (e) => {
		if (lightbox.hidden) return;
		if (e.key === 'Escape')          { e.preventDefault(); close(); }
		else if (e.key === 'ArrowLeft')  { e.preventDefault(); prev();  }
		else if (e.key === 'ArrowRight') { e.preventDefault(); next();  }
	});
}

function buildLightbox() {
	const el = document.createElement('div');
	el.className = 'sentiments-knowhow-lightbox';
	el.hidden = true;
	el.setAttribute('role', 'dialog');
	el.setAttribute('aria-modal', 'true');
	el.setAttribute('aria-label', 'Image viewer');
	el.innerHTML = `
		<button type="button" class="sentiments-knowhow-lightbox__close" aria-label="Close fullscreen image">
			<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false">
				<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
			</svg>
		</button>
		<button type="button" class="sentiments-knowhow-lightbox__prev" aria-label="Previous image">
			<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" focusable="false">
				<path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
			</svg>
		</button>
		<button type="button" class="sentiments-knowhow-lightbox__next" aria-label="Next image">
			<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" focusable="false">
				<path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
			</svg>
		</button>
		<picture class="sentiments-knowhow-lightbox__picture">
			<source class="sentiments-knowhow-lightbox__avif" type="image/avif">
			<source class="sentiments-knowhow-lightbox__webp" type="image/webp">
			<img class="sentiments-knowhow-lightbox__img" alt="" decoding="async">
		</picture>
		<p class="sentiments-knowhow-lightbox__counter"></p>
	`;
	return el;
}

// ─── Video play/pause ──────────────────────────────────────────────────────

function initVideoToggle(root) {
	const video  = root.querySelector('.sentiments-know-how-page__video');
	const toggle = root.querySelector('.sentiments-know-how-page__toggle');
	if (!video) return;

	let userPaused = false;

	const syncToggle = () => {
		if (!toggle) return;
		const isPlaying = !video.paused && !video.ended;
		toggle.dataset.state = isPlaying ? 'playing' : 'paused';
		toggle.setAttribute(
			'aria-label',
			isPlaying ? 'Pause manufacturing video' : 'Play manufacturing video'
		);
		toggle.setAttribute('aria-pressed', String(!isPlaying));
	};

	video.addEventListener('play', syncToggle);
	video.addEventListener('pause', syncToggle);
	syncToggle();

	if (toggle) {
		toggle.addEventListener('click', () => {
			if (video.paused) {
				userPaused = false;
				video.play().catch(() => {});
			} else {
				userPaused = true;
				video.pause();
			}
		});
	}

	if (typeof IntersectionObserver === 'function') {
		const io = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						if (!userPaused && video.paused) {
							video.play().catch(() => {});
						}
					} else if (!video.paused) {
						video.pause();
					}
				}
			},
			{ threshold: 0.01 }
		);
		io.observe(video);
	}
}
